import React, { useState, useEffect, useCallback } from 'react';
import { withTranslation } from '../ChildrenContext.js';
import PropTypes from 'prop-types';
import { withRouter } from '../RouterCompatibility';
import moment from 'moment';
import CollapsibleMultiselect from '../Multiselects/CollapsibleMultiselect';
import DropdownList from 'react-widgets/DropdownList';
import Custom from '../Multiselects/Custom';
import DatePicker from './DataComponent__DatePicker';
import DynamicSelectSegment from '../DynamicSelectSegment';
import ButtonsList from '../ButtonsList';
import InputGroupMultiselect from '../InputGroupMultiselect';
import CustomNotify from '../Notify';
import Button from '../Button';
import debounce from '../../helpers/debounce';
import query from '../../helpers/query';
import config from '../../config';

const initPage = config.initialPage;
const minLength = 3;
const templateKeys = {
    datepicker: 'getDatepicker',
    select: 'getSelect',
    multiselect: 'getMultiselect',
    dropdown: 'getDropdown',
    textInput: 'getTextInput',
    custom: 'getCustom',
    dynamic: 'getDynamic',
    goalTree: 'getSelectGoalTree',
    currency: 'getNull' //test
};

const DataComponentFilterOffer = ({
    fields: propFields,
    location,
    startDate: propStartDate,
    endDate: propEndDate,
    loading: propLoading,
    hideResetButton: propHideResetButton,
    gray: propGray,
    onAction: propOnAction,
    t
}) => {
    const parsedQuery = query.parse(location && location.query);
    
    const getDefValues = useCallback((fields = []) => {
        let values = {};

        (fields || []).map(item => {
            if (!item.value) return;

            if (item.name === 'datepicker') {
                item.value.startDate && (values[`${item.key}StartDate`] = item.value.startDate);
                item.value.endDate && (values[`${item.key}EndDate`] = item.value.endDate);
            } else {
                values[item.key] = item.value;
            }
        });

        return values;
    }, []);

    const getSelected = useCallback((fields = [], values = {}) => {
        let selected = [];

        (fields || []).map((item, i) => {
            if (item.name === 'datepicker' && values[`${item.key}StartDate`]
                || values[item.key]) {
                selected.push({
                    ...item,
                    fieldIndex: i
                });
            }
        });

        return selected;
    }, []);

    const [values, setValues] = useState(() => {
        let initialValues = {
            page: parsedQuery.page || initPage,
            ...getDefValues(propFields),
            ...parsedQuery
        };

        if ("/office/finances/report" !== location.pathname) {
            initialValues.createStartDate = parsedQuery.createStartDate || parsedQuery.startDate || propStartDate || moment().subtract(6, 'days');
            initialValues.createEndDate = parsedQuery.createEndDate || parsedQuery.endDate || propEndDate || moment().endOf('day');
        }

        return initialValues;
    });

    const [selected, setSelected] = useState(() => getSelected(propFields, values));

    const staticNames = (t && t.dataTable) || {};
    const staticNamesActions = (t && t.actions) || {};
    
    const messagesForSelect = {
        emptyList: staticNames.emptyList
    };

    const textInputChange = useCallback(debounce((name) => {
        if (values[name].length > 0 && values[name].length <= minLength) {
            let message = t.validate.minLineLength.replace(/%s/i, minLength);
            CustomNotify(message, 'error');
        }
    }, 500), [values, t]);

    const deleteValue = useCallback((values, item) => {
        if (item.name === 'datepicker') {
            delete values[`${item.key}StartDate`];
            delete values[`${item.key}EndDate`];
        } else {
            delete values[item.key];
        }
    }, []);

    const getValue = useCallback((data) => {
        if (data.name === 'datepicker') {
            return {
                startDate: values[`${data.key}StartDate`] || moment().subtract(6, 'days'),
                endDate: values[`${data.key}EndDate`] || moment().endOf('day')
            };
        } else if (data.name === 'textInput') {
            return values[data.key] || '';
        }

        return values[data.key];
    }, [values]);

    const getNull = useCallback(() => {
        return null;
    }, []);

    const onDataPickerApply = useCallback((datepicker, key) => {
        setValues(prevValues => ({
            ...prevValues,
            [`${key}StartDate`]: datepicker.startDate.clone().startOf('day'),
            [`${key}EndDate`]: datepicker.endDate.clone().endOf('day')
        }));
    }, []);

    const onTextInputChange = useCallback((event) => {
        const name = event.target.name;

        setValues(prevValues => ({
            ...prevValues,
            [name]: event.target.value
        }));
        
        textInputChange(name);
    }, [textInputChange]);

    const onSelectChange = useCallback((event) => {
        const name = event.target.name;

        setValues(prevValues => ({
            ...prevValues,
            [name]: event.target.value
        }));
    }, []);

    const onChange = useCallback((value, key) => {
        setValues(prevValues => ({
            ...prevValues,
            [key]: value
        }));
        propOnAction({...values, [key]: value});
    }, [values, propOnAction]);

    const onChangeDynamic = useCallback((value, key) => {
        const newValue = (value || []).map(item => {return {title: item.title, value: item.value}});

        setValues(prevValues => ({
            ...prevValues,
            [key]: newValue
        }));
    }, []);

    const onAction = useCallback((type) => {
        setValues(prevValues => ({
            ...prevValues,
            page: initPage
        }));
        propOnAction({...values, page: initPage}, type);
    }, [values, propOnAction]);

    const onChangeFilter = useCallback((item, index) => {
        const fieldIndex = propFields.findIndex(data => data.key === item.key);
        let newSelected = [...selected];
        let newValues = {...values};
        let oldItem = newSelected[index];

        deleteValue(newValues, oldItem);
        newSelected[index] = {
            ...item,
            fieldIndex
        };

        setSelected(newSelected);
        setValues(newValues);
    }, [selected, values, propFields, deleteValue]);

    const onAddFilter = useCallback((item) => {
        const fieldIndex = propFields.findIndex(data => data.key === item.key);
        let newSelected = [...selected];

        newSelected.push({
            ...item,
            fieldIndex
        });

        setSelected(newSelected);
    }, [selected, propFields]);

    const onDelete = useCallback((item, index) => {
        let newSelected = [...selected];
        let newValues = {...values};
        let deletedItem = newSelected.find( ({ name }) => name === item.name );

        index = index === undefined ? newSelected.indexOf(deletedItem) : index;

        deleteValue(newValues, deletedItem);
        newSelected.splice(index, 1);

        setSelected(newSelected);
        setValues(newValues);
    }, [selected, values, deleteValue]);

    useEffect(() => {
        onAction('firstRender');
    }, []);

    const getDatepicker = useCallback((data) => {
        const onDataPickerApplyCallback = (item) => onDataPickerApply(item, data.key);
        return (
            <DatePicker
                onApply={onDataPickerApplyCallback}
                className="btn form-control reactdaypicker-block"
                {...getValue(data)}
            />
        );
    }, [onDataPickerApply, getValue]);

    const getSelect = useCallback((select) => {
        return (
            <select
                onChange={onSelectChange}
                className="form-control long-input right"
                name={select.key}
                value={getValue(select)}
            >
                {(propFields[select.fieldIndex].data || []).map((option, i) => (
                    <option key={i} value={option.value}>{option.title}</option>
                ))}
            </select>
        );
    }, [onSelectChange, getValue, propFields]);

    const getDropdown = useCallback((dropdown) => {
        return (
            <DropdownList
                messages={messagesForSelect}
                className={'rw-dropdownlist-select'}
                dataKey="value"
                textField='title'
                placeholder={dropdown.placeholder || ''}
                data={propFields[dropdown.fieldIndex].data}
                value={getValue(dropdown)}
                onChange={(value) => { onChange(value, dropdown.key); }}
            />
        );
    }, [messagesForSelect, getValue, propFields, onChange]);

    const getMultiselect = useCallback((select) => {
        return (
            <CollapsibleMultiselect
                messages={messagesForSelect}
                placeholder={select.placeholder || ''}
                className='right'
                dataKey="value"
                textField='title'
                onChange={(value) => { onChange(value, select.key); }}
                data={propFields[select.fieldIndex].data}
                name={select.key}
                value={getValue(select)}
            />
        );
    }, [messagesForSelect, onChange, propFields, getValue]);

    const getTextInput = useCallback((data) => {
        return (
            <input
                className="form-control"
                type="text"
                name={data.key}
                onChange={onTextInputChange}
                placeholder={data.placeholder}
                value={getValue(data)}
            />
        );
    }, [onTextInputChange, getValue]);

    const getCustom = useCallback((data) => {
        const onChangeCustom = (item) => onChange(item, data.key);

        return (
            <Custom
                segment={data.label}
                values={getValue(data)}
                onAction={onChangeCustom}
            />
        );
    }, [onChange, getValue]);

    const getDynamic = useCallback((data) => {
        const onChangeDynamicCallback = (item) => onChangeDynamic(item, data.key);

        return (
            <DynamicSelectSegment
                segment={data.segment}
                multiselect={true}
                values={getValue(data)}
                onAction={onChangeDynamicCallback}
            />
        );
    }, [onChangeDynamic, getValue]);

    const getSelectGoalTree = useCallback((data) => {
        const onChangeCallback = (item) => onChange(item, data.key);

        return (
            <InputGroupMultiselect
                nesting={data.nesting}
                onChange={onChangeCallback}
                tags={getValue(data)}
            />
        );
    }, [onChange, getValue]);

    const getChangeFunction = useCallback((value, key, name) => {
        if(name === 'currency' ||  key === 'currency') {
            return onChange(value, key);
        } else if(name === 'datepicker') {
            return onDataPickerApply(value, key);
        } else {
            alert('No function assigned');
        }
    }, [onChange, onDataPickerApply]);
    
    const renderFilter = useCallback((item = {}, i) => {
        const onChangeFilterCallback = (item) => onChangeFilter(item, i);
        const onChangeDropdown = (i >= 0) ? onChangeFilterCallback : onAddFilter;
        const onDeleteCallback = () => onDelete(item, i);
        
        return (
            <div className={`table-filter ${propGray ? ' table-filter-gray' : ''}`}  key={item.key}>
                {item.withoutDropdown
                    ? null
                    : (<div className="table-filter__item">
                        <label> {item.dropdownLabel || staticNames.addFilter} </label>
                        <DropdownList
                            className={'rw-dropdownlist-select'}
                            placeholder={staticNames.addFilter }
                            dataKey="key"
                            textField='label'
                            data={propFields}
                            disabled={item.disabled || selected}
                            value={item.label || ''}
                            onChange={onChangeDropdown}
                        />
                    </div>)
                }
                {item.name
                    ? (
                        <React.Fragment>
                            <div className="table-filter__item payouts-block">
                                {item.label && <label> {item.label}
                                    {!["datepicker", "custom", "multiselect", "dynamic", "goalTree"].includes(item.name) ? (<i className="fa fa-search"></i>) : null}
                                </label>}
                                {templateKeys[item.name] && (() => {
                                    const methodName = templateKeys[item.name].replace('get', '');
                                    switch(methodName) {
                                        case 'Datepicker': return getDatepicker(item);
                                        case 'Select': return getSelect(item);
                                        case 'Multiselect': return getMultiselect(item);
                                        case 'Dropdown': return getDropdown(item);
                                        case 'TextInput': return getTextInput(item);
                                        case 'Custom': return getCustom(item);
                                        case 'Dynamic': return getDynamic(item);
                                        case 'SelectGoalTree': return getSelectGoalTree(item);
                                        case 'Null': return getNull();
                                        default: return null;
                                    }
                                })()}
                            </div>
                            {!item.disabled || 'datepicker' === item.name
                                ? (
                                    <div className="table-filter__item control-buttons">
                                        <Button
                                           loading={propLoading}
                                           extraClass={{'apply-btn': true}}
                                           text={staticNamesActions.apply}
                                           onClick={onAction}
                                       />
                                        <Button
                                            extraClass={{'reset-btn': true, 'hidden': propHideResetButton}}
                                            text={staticNamesActions.reset}
                                            onClick={onDeleteCallback}
                                        />
                                    </div>
                                )
                                : null
                            }
                        </React.Fragment>
                    )
                    : null
                }
                {item.withList 
                    ? <ButtonsList 
                        fields={propFields} 
                        data={item} 
                        onChange={(value, key) => getChangeFunction(value, key, item.name)} 
                        currentValue={values[item.key]} 
                        showSubtitle={item.showSubtitle}
                       />
                    : null
                }
            </div>
        );
    }, [propGray, staticNames, propFields, selected, propLoading, staticNamesActions, propHideResetButton, onAction, onDelete, onChangeFilter, onAddFilter, getChangeFunction, values, getDatepicker, getSelect, getMultiselect, getDropdown, getTextInput, getCustom, getDynamic, getSelectGoalTree, getNull]);

    if (!propFields.length) return null;
    
    return (
        <div className="ibox-title hidden">
            {(selected || []).map( filter => renderFilter(filter))}
            {propFields.length !== selected.length
                ? renderFilter()
                : null
            }
        </div>
    );
};

DataComponentFilterOffer.propTypes = {
    fields: PropTypes.array,
    location: PropTypes.object,
    startDate: PropTypes.object,
    endDate: PropTypes.object,
    loading: PropTypes.bool,
    hideResetButton: PropTypes.bool,
    gray: PropTypes.bool,
    onAction: PropTypes.func.isRequired,
    t: PropTypes.object
};

DataComponentFilterOffer.defaultProps = {
    fields: [],
    loading: false,
    hideResetButton: false,
    gray: false
};

export default withRouter(withTranslation(DataComponentFilterOffer));